import React, { useState } from "react";
import { useAuth } from "../../../../context/authContext";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Card,
  Row,
  Col,
  Radio,
  Upload,
  Switch,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
  BarsOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { SketchPicker } from "react-color";
import useFeedbackFormService from "../../../../services/feedbackFormService";
import useCloudinaryService from "../../../../services/cloudinaryService";
import StarRating from "./StarRating";
import DotMatrixBackground from "../../../FeedbackForm/templates/DotMatrixBackground";
import BubbleBackground from "../../../FeedbackForm/templates/BubbleBackground";
import DiagonalLinesBackground from "../../../FeedbackForm/templates/DiagonalBackground";
import WaveBackground from "../../../FeedbackForm/templates/WaveBackground";
import GridBackground from "../../../FeedbackForm/templates/GridBackground";
import SubtleBackground from "../../../FeedbackForm/templates/SubtleBackground";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const { Option } = Select;

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    border: "1px solid #eee",
    padding: 12,
    borderRadius: 4,
    background: "#fff",
    marginBottom: 8,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

const FeedbackModal = ({
  visible,
  isEditing,
  currentForm,
  onClose,
  onSubmit,
  forms,
}) => {
  const [form] = Form.useForm();
  const [selectedDesign, setSelectedDesign] = useState("Design 6");
  const [color, setColor] = useState("#4F46E5");
  const { uploadFormLogo } = useCloudinaryService();
  const [logoFile, setLogoFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const { subscriptionLevel, fetchUser } = useAuth();
  const { language } = useLanguage();
  const { updateFeedbackForm, createFeedbackForm } = useFeedbackFormService();
  const text = dashboardContent[language].feedback.modal;
  const questions = form.getFieldValue("questions") || [];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Premium Overlay Text based on language
  const premiumOverlayText = {
    DE: {
      title: "Premium Features",
      features: [
        "Logo-Upload für Ihre Formulare",
        "Google Review Integration",
        "Foto-Upload in Feedback-Formularen",
      ],
      upgradeButton: "Jetzt upgraden",
    },
    EN: {
      title: "Premium Features",
      features: [
        "Logo upload for your forms",
        "Google Review integration",
        "Photo upload in feedback forms",
      ],
      upgradeButton: "Upgrade now",
    },
  };

  const currentPremiumText =
    premiumOverlayText[language] || premiumOverlayText.DE;

  const handleColorChange = (color) => {
    setColor(color.hex);
  };

  React.useEffect(() => {
    if (visible && isEditing && currentForm) {
      const transformedQuestions = currentForm.questions
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((q, idx) => ({
          _id: q._id,
          question: q.questionText,
          type: q.type,
          options: q.options || [],
          order: q.order != null ? q.order : idx,
        }));

      form.setFieldsValue({
        name: currentForm.name,
        description: currentForm.description,
        status: currentForm.status,
        questions: transformedQuestions,
        enableGoogleRedirect: currentForm.enableGoogleRedirect || false,
        googleReviewUrl: currentForm.googleReviewUrl || "",
        language: currentForm.language || "DE",
        enablePhotos: currentForm.enablePhotos || false,
      });

      if (currentForm.logo) {
        setLogoFile(currentForm.logo);
        setFileList([
          {
            uid: "-1",
            name: text.uploadFileDefaultName,
            status: "done",
            url: currentForm.logo,
          },
        ]);
      } else {
        setFileList([]);
      }
      if (currentForm.design) {
        setSelectedDesign(currentForm.design);
      }
      if (currentForm.designColor) {
        setColor(currentForm.designColor);
      }
    } else {
      form.resetFields();
      setLogoFile(null);
      setSelectedDesign("Design 6");
      setColor("#4F46E5");
      setFileList([]);
    }
  }, [isEditing, currentForm, form, visible]);

  const handleFinish = async (values) => {
    let logoUrl = currentForm?.logo;
    const formLang = values.language || "DE";

    const yesNoOptions = formLang === "DE" ? ["Ja", "Nein"] : ["Yes", "No"];

    try {
      if (isEditing && currentForm) {
        if (logoFile && typeof logoFile !== "string") {
          message.loading({
            content: text.logoUploadLoading,
            key: "upload",
          });
          logoUrl = await uploadFormLogo(logoFile);
          if (logoUrl) {
            message.success({
              content: text.logoUploadSuccess,
              key: "upload",
            });
          } else {
            throw new Error(text.logoUploadError);
          }
        }

        const questionsWithOrder = (values.questions || []).map((q, idx) => {
          const opts = q.type === "yesno" ? yesNoOptions : q.options;
          return {
            _id: q._id,
            question: q.question,
            type: q.type,
            options: opts,
            order: idx,
          };
        });

        const payload = {
          name: values.name,
          description: values.description,
          status: values.status,
          questions: questionsWithOrder,
          logo: logoUrl,
          design: selectedDesign,
          designColor: color,
          enableGoogleRedirect: values.enableGoogleRedirect || false,
          googleReviewUrl: values.googleReviewUrl || "",
          enablePhotos: values.enablePhotos,
          language: values.language,
        };

        const result = await updateFeedbackForm(currentForm._id, payload);
        if (result.success) {
          const updatedForms = forms.map((form) =>
            form._id === currentForm._id ? { ...form, ...payload } : form
          );
          onSubmit(updatedForms);
        }
      } else {
        if (logoFile) {
          message.loading({
            content: "Logo wird hochgeladen...",
            key: "upload",
          });
          logoUrl = await uploadFormLogo(logoFile);
          if (logoUrl) {
          } else {
            throw new Error("Fehler beim Hochladen des Logos");
          }
        }

        const questionsWithOrder = (values.questions || []).map((q, idx) => {
          const opts = q.type === "yesno" ? yesNoOptions : q.options;
          return {
            question: q.question,
            type: q.type,
            options: opts,
            order: idx,
          };
        });
        const payload = {
          name: values.name,
          description: values.description,
          status: values.status,
          questions: questionsWithOrder,
          logo: logoUrl || "",
          design: selectedDesign,
          designColor: color,
          enableGoogleRedirect: values.enableGoogleRedirect || false,
          googleReviewUrl: values.googleReviewUrl || "",
          enablePhotos: values.enablePhotos,
          language: values.language,
        };

        const result = await createFeedbackForm(payload);
        if (result && result.feedbackForm) {
          const newForm = {
            _id: result.feedbackForm._id,
            name: result.feedbackForm.name,
            description: result.feedbackForm.description,
            status: result.feedbackForm.status,
            feedbackCount: 0,
            questions: result.feedbackForm.questions,
            logo: logoUrl,
            design: selectedDesign,
            designColor: color,
          };
          message.success(
            isEditing ? text.formUpdateSuccess : text.formCreateSuccess
          );
          await fetchUser();
          onSubmit([...forms, newForm]);
        }
      }
    } catch (error) {
      console.error(
        `Fehler beim ${
          isEditing ? "Aktualisieren" : "Erstellen"
        } des Formulars:`,
        error.message
      );
      message.error(isEditing ? text.formUpdateError : text.formCreateError);
    }
  };

  const handleDesignChange = (e) => {
    setSelectedDesign(e.target.value);
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error(text.uploadError);
        return Upload.LIST_IGNORE;
      }
      setLogoFile(file);
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
        },
      ]);
      return false;
    },
    fileList,
    showUploadList: { showRemoveIcon: true },
    onRemove: () => {
      setLogoFile(null);
      setFileList([]);
    },
    disabled: subscriptionLevel < 2,
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const questions = form.getFieldValue("questions") || [];
    const oldIndex = Number(active.id);
    const newIndex = Number(over.id);
    const reordered = arrayMove(questions, oldIndex, newIndex);
    form.setFieldsValue({ questions: reordered });
  };

  const designs = [
    <DotMatrixBackground isCardCover={true} bgColor={color} />,
    <BubbleBackground isCardCover={true} bgColor={color} />,
    <DiagonalLinesBackground isCardCover={true} bgColor={color} />,
    <WaveBackground isCardCover={true} bgColor={color} />,
    <GridBackground isCardCover={true} bgColor={color} />,
    <SubtleBackground isCardCover={true} bgColor={color} />,
  ];

  return (
    <Modal
      title={isEditing ? text.titleEdit : text.titleCreate}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width="95%"
      style={{ maxWidth: 800 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="space-y-4  max-h-[70vh] md:max-h-[80vh] overflow-y-scroll custom-scrollbar px-4"
      >
        <Form.Item
          name="name"
          label={text.nameLabel}
          rules={[{ required: true, message: text.formNameRequired }]}
        >
          <Input placeholder={text.namePlaceholder} />
        </Form.Item>

        <Form.Item
          name="description"
          label={text.descriptionLabel}
          rules={[{ required: true, message: text.formDescriptionRequired }]}
        >
          <Input.TextArea placeholder={text.descriptionPlaceholder} rows={4} />
        </Form.Item>

        <Form.Item
          name="status"
          label={text.statusLabel}
          rules={[{ required: true, message: text.formStatusRequired }]}
        >
          <Select placeholder={text.statusPlaceholder}>
            <Option value="active">{text.statusActive}</Option>
            <Option value="inactive">{text.statusInactive}</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="language"
          label={text.languageLabel}
          rules={[{ required: true, message: text.languageRequired }]}
        >
          <Select placeholder={text.languagePlaceholder}>
            <Option value="DE">Deutsch</Option>
            <Option value="EN">English</Option>
          </Select>
        </Form.Item>

        <h2>{text.questionLabel}</h2>
        <Form.List name="questions">
          {(fields, { add, remove }) => {
            const questions = form.getFieldValue("questions") || [];

            return (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={fields.map((field) => field.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {fields.map((field, index) => {
                      const id = field.key;
                      const qType = form.getFieldValue([
                        "questions",
                        index,
                        "type",
                      ]);

                      return (
                        <SortableItem key={`${field.key}-${index}`} id={id}>
                          <div className="p-4 border rounded bg-white mb-2">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium">
                                {index + 1}.{" "}
                                {form.getFieldValue([
                                  "questions",
                                  index,
                                  "question",
                                ]) || text.questionPlaceholder}
                              </span>
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                danger
                                onClick={() => remove(field.name)}
                              />
                            </div>

                            <Form.Item
                              {...field}
                              name={[field.name, "question"]}
                              fieldKey={[field.fieldKey, "question"]}
                              rules={[
                                {
                                  required: true,
                                  message: text.questionRequired,
                                },
                              ]}
                            >
                              <Input placeholder={text.questionPlaceholder} />
                            </Form.Item>

                            <Form.Item
                              {...field}
                              name={[field.name, "type"]}
                              fieldKey={[field.fieldKey, "type"]}
                              hidden
                            >
                              <Input type="hidden" />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              name={[field.name, "_id"]}
                              fieldKey={[field.fieldKey, "_id"]}
                              hidden
                            >
                              <Input type="hidden" />
                            </Form.Item>

                            {qType === "multiple-choice" && (
                              <>
                                <Form.List name={[field.name, "options"]}>
                                  {(
                                    optFields,
                                    { add: addOpt, remove: removeOpt }
                                  ) => (
                                    <>
                                      {optFields.map((o) => (
                                        <div
                                          key={o.key}
                                          className="flex items-center space-x-2 mb-2"
                                        >
                                          <Form.Item
                                            {...o}
                                            rules={[
                                              {
                                                required: true,
                                                message: text.optionRequired,
                                              },
                                            ]}
                                          >
                                            <Input
                                              placeholder={
                                                text.optionPlaceholder
                                              }
                                            />
                                          </Form.Item>
                                          <Button
                                            type="text"
                                            icon={<MinusCircleOutlined />}
                                            danger
                                            onClick={() => removeOpt(o.name)}
                                          />
                                        </div>
                                      ))}
                                      <Form.Item>
                                        <Button
                                          type="dashed"
                                          icon={<PlusCircleOutlined />}
                                          disabled={optFields.length >= 6}
                                          onClick={() => addOpt()}
                                        >
                                          {text.addOption}
                                        </Button>
                                      </Form.Item>
                                    </>
                                  )}
                                </Form.List>
                                {/* Kompakte Info für Multiple Choice */}
                                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                  <div className="flex items-center space-x-1">
                                    <InfoCircleOutlined className="text-blue-500 text-sm" />
                                    <span className="text-blue-800 font-medium">
                                      {language === "DE"
                                        ? "Mehrfachauswahl möglich"
                                        : "Multiple selection possible"}
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}

                            {qType === "yesno" && (
                              <>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "choice"]}
                                  fieldKey={[field.fieldKey, "choice"]}
                                  initialValue="Ja"
                                  rules={[
                                    {
                                      required: true,
                                      message: text.chooseYesNo,
                                    },
                                  ]}
                                >
                                  <Select
                                    placeholder={text.chooseYesNoPlaceholder}
                                  >
                                    <Option value="Ja">Ja</Option>
                                    <Option value="Nein">Nein</Option>
                                  </Select>
                                </Form.Item>
                                {/* Kompakte Info für Yes/No */}
                                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                                  <div className="flex items-center space-x-1">
                                    <InfoCircleOutlined className="text-green-500 text-sm" />
                                    <span className="text-green-800 font-medium">
                                      {language === "DE"
                                        ? "Automatische Anschlussfrage nach Ja/Nein"
                                        : "Automatic follow-up question after Yes/No"}
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}

                            {qType === "star-rating" && <StarRating />}
                          </div>
                        </SortableItem>
                      );
                    })}
                  </SortableContext>
                </DndContext>
                <Form.Item>
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={8}>
                      <Button
                        type="dashed"
                        icon={<EditOutlined />}
                        className="w-full bg-orange-500 text-white hover:!text-white hover:!bg-orange-600 border border-orange-600 shadow-md transition-all duration-200 ease-in-out transform hover:scale-105"
                        block
                        onClick={() =>
                          add({ type: "text", question: "", options: [] })
                        }
                      >
                        {text.addText}
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        type="dashed"
                        icon={<BarsOutlined />}
                        className="w-full bg-orange-500 text-white hover:!text-white hover:!bg-orange-600 border border-orange-600 shadow-md transition-all duration-200 ease-in-out transform hover:scale-105"
                        block
                        onClick={() =>
                          add({
                            type: "multiple-choice",
                            question: "",
                            options: [],
                          })
                        }
                      >
                        {text.addMC}
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        type="dashed"
                        icon={<StarOutlined />}
                        className="w-full bg-orange-500 text-white hover:!text-white hover:!bg-orange-600 border border-orange-600 shadow-md transition-all duration-200 ease-in-out transform hover:scale-105"
                        block
                        onClick={() =>
                          add({
                            type: "star-rating",
                            question: "",
                            options: [],
                          })
                        }
                      >
                        {text.addStar}
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Tooltip title={text.dynamicInfo} placement="top">
                        <Button
                          type="dashed"
                          icon={<PlusCircleOutlined />}
                          className="w-full bg-orange-500 text-white hover:!bg-orange-600 hover:!text-white border border-orange-600 shadow-md transition-all duration-200 ease-in-out transform hover:scale-105"
                          block
                          onClick={() =>
                            add({ type: "yesno", question: "", options: [] })
                          }
                        >
                          {text.addDynamic}
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                </Form.Item>
              </>
            );
          }}
        </Form.List>

        <Form.Item label={text.designLabel}>
          <Radio.Group onChange={handleDesignChange} value={selectedDesign}>
            <Row gutter={[16, 16]}>
              {designs.map((design, index) => (
                <Col key={design} xs={12} sm={8}>
                  <Radio
                    value={`Design ${index + 1}`}
                    style={{ display: "block" }}
                  >
                    <Card
                      hoverable
                      cover={design}
                      style={{ textAlign: "center" }}
                    >
                      {text.designTitlePrefix} {index + 1}
                    </Card>
                  </Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Form.Item>

        <Form.Item label={text.colorLabel}>
          <SketchPicker color={color} onChange={handleColorChange} />
        </Form.Item>

        {/* Premium Features Section */}
        <div className="relative mt-6">
          {subscriptionLevel < 2 && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
              <div className="text-center p-6 bg-white rounded-lg shadow-lg border border-orange-200 max-w-sm mx-auto">
                <div className="mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <StarOutlined className="text-orange-500 text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {currentPremiumText.title}
                  </h3>
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  <ul className="space-y-1 text-left">
                    {currentPremiumText.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-orange-500 mr-2">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  type="primary"
                  className="bg-orange-500 border-orange-500 hover:!bg-orange-600 hover:!border-orange-600"
                  onClick={() => (window.location.href = "/pricing")}
                >
                  {currentPremiumText.upgradeButton}
                </Button>
              </div>
            </div>
          )}

          <div className={subscriptionLevel < 2 ? "opacity-30" : ""}>
            <Form.Item label={text.logoLabel} name="logo">
              <Upload {...uploadProps} listType="picture" maxCount={1}>
                <Button
                  icon={<UploadOutlined />}
                  disabled={subscriptionLevel < 2}
                >
                  Logo hochladen
                </Button>
              </Upload>
            </Form.Item>

            <Form.Item
              name="enableGoogleRedirect"
              label={text.googleReviewToggleLabel}
              valuePropName="checked"
            >
              <Switch
                className="custom-orange-switch"
                disabled={subscriptionLevel < 2}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.enableGoogleRedirect !==
                currentValues.enableGoogleRedirect
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("enableGoogleRedirect") ? (
                  <Form.Item
                    name="googleReviewUrl"
                    label={text.googleReviewUrlLabel}
                    rules={[
                      {
                        required: true,
                        message: text.googleReviewUrlRequired,
                      },
                      {
                        type: "url",
                        message: text.googleReviewUrlInvalid,
                      },
                    ]}
                  >
                    <Input
                      placeholder={text.googleReviewUrlPlaceholder}
                      disabled={subscriptionLevel < 2}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="enablePhotos"
              label={text.photosLabel}
              valuePropName="checked"
            >
              <Switch
                className="custom-orange-switch"
                disabled={subscriptionLevel < 2}
              />
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <Button onClick={onClose}>{text.cancel}</Button>
          <Button
            className="px-4 py-2 rounded text-white bg-orange-500 hover:bg-orange-600"
            htmlType="submit"
          >
            {isEditing ? text.update : text.create}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default FeedbackModal;
